import {
  Archive,
  Eye,
  FolderOpen,
  Tag,
  MoreVertical,
  Pencil,
  Trash2,
  Plus,
  ShieldAlert,
} from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ClassificationConfig, ClassificationTargets } from './config'
import { useId, useState } from 'react'
import { queryKeys, useMailboxResources } from '@/api'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ResourceDialog, type ResourceEditor } from './resource-dialog'
import { resourceName } from './resource-name'
import { classificationRequestMessage } from './errors'

export function MailboxPermissions({
  inboxId,
  config,
  targets,
  gmail,
  onChange,
  readOnly = false,
  targetsFailed = false,
  onRetryTargets,
}: {
  inboxId: string
  config: ClassificationConfig
  targets?: ClassificationTargets
  gmail: boolean
  onChange: (next: ClassificationConfig) => void
  readOnly?: boolean
  // Without targets the mailbox's labels and folders aren't known yet.
  targetsFailed?: boolean
  onRetryTargets?: () => void
}) {
  const prefix = useId()
  const catalog = useMailboxResources(inboxId)
  const queryClient = useQueryClient()
  const refreshResources = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.classification(inboxId),
    })
  const [editor, setEditor] = useState<ResourceEditor>()
  const permissions = config.permissions
  const movement = gmail ? targets?.labels : targets?.folders
  const groups = [
    {
      key: 'mark_read',
      title: 'Mark as read',
      icon: Eye,
      available: targets?.capabilities.mark_read,
    },
    {
      key: 'archive',
      title: 'Archive',
      icon: Archive,
      available: targets?.capabilities.archive,
    },
    {
      key: 'move',
      title: gmail ? 'Move under a label' : 'Move to a folder',
      icon: FolderOpen,
      available: targets?.capabilities.destination,
    },
    {
      key: 'labels',
      title: 'Add labels',
      icon: Tag,
      available: targets?.capabilities.labels,
    },
    {
      key: 'categories',
      title: 'Add categories',
      icon: Tag,
      available: targets?.capabilities.categories,
    },
  ] as const
  const choices = (
    title: string,
    resources: ClassificationTargets['folders'] | undefined,
    key:
      'allowed_move_target_ids' | 'allowed_label_ids' | 'allowed_category_ids',
  ) => {
    const selected = permissions[key]
    // Only a loaded list can say a selected label or folder is gone.
    const missing = targets
      ? selected.filter((id) => !resources?.some((resource) => resource.id === id))
      : []
    const kind =
      key === 'allowed_move_target_ids'
        ? gmail
          ? 'label'
          : 'folder'
        : key === 'allowed_label_ids'
          ? 'label'
          : 'category'
    const collection =
      kind === 'label' ? 'labels' : kind === 'folder' ? 'folders' : 'categories'
    const management = catalog.data?.management[collection]
    return (
      <fieldset className="space-y-1 border-t px-4 py-3 [&>:not(legend)]:clear-left">
        <legend className="float-left mb-1 w-full text-sm font-medium">
          {title}
        </legend>
        {!targets && !targetsFailed && (
          <div role="status" className="clear-left space-y-2 px-2 py-2">
            <span className="sr-only">Loading {collection}…</span>
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-3">
                <Skeleton className="size-4 shrink-0 rounded" />
                <Skeleton className="h-4 w-40 max-w-full" />
              </div>
            ))}
          </div>
        )}
        {!targets && targetsFailed && (
          <div className="clear-left flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-2 text-sm">
            <span className="text-muted-foreground">Couldn’t load {collection}.</span>
            {onRetryTargets && (
              <Button variant="link" size="sm" className="h-auto p-0" onClick={onRetryTargets}>
                Reload
              </Button>
            )}
          </div>
        )}
        {targets && !resources?.length && (
          <p className="clear-left text-sm text-muted-foreground">
            None yet.
          </p>
        )}
        {resources?.map((resource) => {
          const managed = catalog.data?.[collection].find(
            (item) => item.id === resource.id,
          )
          const editable =
            managed &&
            ((managed.can_rename && management?.rename) ||
              (kind === 'category' && management?.edit_color))
          const deletable = managed?.can_delete && management?.delete
          return (
            <div
              key={resource.id}
              className="clear-left flex items-center rounded-lg hover:bg-muted/30"
            >
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 px-2 py-2">
                <input
                  type="checkbox"
                  className="size-4 shrink-0 accent-primary"
                  checked={selected.includes(resource.id)}
                  onChange={(event) =>
                    onChange({
                      ...config,
                      permissions: {
                        ...permissions,
                        [key]: event.target.checked
                          ? [...selected, resource.id]
                          : selected.filter((id) => id !== resource.id),
                      },
                    })
                  }
                />
                <span className="min-w-0 break-words text-sm">
                  {resourceName(resource, resources ?? [])}
                </span>
              </label>
              {!readOnly && managed && (editable || deletable) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Options for ${resource.name} ${kind}`}
                    >
                      <MoreVertical className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {editable && (
                      <DropdownMenuItem
                        onClick={() =>
                          setEditor({ kind, mode: 'edit', resource: managed })
                        }
                      >
                        <Pencil className="mr-2 size-4" />
                        {kind === 'category' ? 'Edit color' : 'Rename'}
                      </DropdownMenuItem>
                    )}
                    {deletable && (
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() =>
                          setEditor({ kind, mode: 'delete', resource: managed })
                        }
                      >
                        <Trash2 className="mr-2 size-4" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {!readOnly && !managed && catalog.isError && (
                <span className="px-2 text-xs text-muted-foreground">
                  Reload to edit
                </span>
              )}
            </div>
          )
        })}
        {missing.map((id) => (
          <label
            key={id}
            className="clear-left flex items-center gap-3 px-2 py-2 text-sm text-destructive"
          >
            <input
              type="checkbox"
              className="size-4 shrink-0 accent-primary"
              checked
              onChange={() =>
                onChange({
                  ...config,
                  permissions: {
                    ...permissions,
                    [key]: selected.filter((value) => value !== id),
                  },
                })
              }
            />
            A {kind} that no longer exists — untick to remove
          </label>
        ))}
        {!readOnly && management?.create && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditor({ kind, mode: 'create' })}
          >
            <Plus className="size-4" />
            Create {kind}
          </Button>
        )}
        {kind === 'folder' &&
          catalog.data?.folders.some(
            (folder) => !folder.can_rename && !folder.can_delete,
          ) && (
            <p className="text-xs text-muted-foreground">
              Built-in folders can’t be renamed or deleted.
            </p>
          )}
        {!readOnly &&
          management &&
          !management.create &&
          !management.rename &&
          !management.edit_color &&
          !management.delete && (
            <p className="text-xs text-muted-foreground">
              Reconnect this inbox to manage {collection}.
            </p>
          )}
      </fieldset>
    )
  }
  return (
    <section className="space-y-3">
      <h2 className="font-semibold">Allowed actions</h2>
        {groups
          .filter((group) => group.key !== (gmail ? 'categories' : 'labels'))
          .map((group) => (
            <div key={group.key} className="rounded-xl border">
              <div className="flex items-center gap-3 p-4">
                <group.icon className="size-4 shrink-0 text-muted-foreground" />
                <label
                  htmlFor={`${prefix}-permission-${group.key}`}
                  className="flex-1 text-sm font-medium"
                >
                  {group.title}
                </label>
                <Switch
                  id={`${prefix}-permission-${group.key}`}
                  checked={permissions[group.key]}
                  disabled={group.available !== true && !permissions[group.key]}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...config,
                      permissions: { ...permissions, [group.key]: checked },
                    })
                  }
                />
              </div>
              {group.key !== 'mark_read' && group.key !== 'archive' && (
                <p className="-mt-2 px-4 pb-3 pl-11 text-xs text-muted-foreground">
                  {group.key === 'move'
                    ? 'Takes email out of the inbox.'
                    : 'Leaves email in the inbox.'}
                </p>
              )}
              {group.available === false && (
                <p className="-mt-2 px-4 pb-3 pl-11 text-xs text-muted-foreground">
                  Reconnect this inbox to allow this.
                </p>
              )}
              {group.key === 'move' &&
                permissions.move &&
                choices(
                  gmail ? 'Labels it can move email to' : 'Folders it can move email to',
                  movement,
                  'allowed_move_target_ids',
                )}
              {group.key === 'labels' &&
                permissions.labels &&
                choices('Labels it can add', targets?.labels, 'allowed_label_ids')}
              {group.key === 'categories' &&
                permissions.categories &&
                choices(
                  'Categories it can add',
                  targets?.categories,
                  'allowed_category_ids',
                )}
            </div>
          ))}
        {(['spam', 'phishing'] as const).map((key) => {
          const folderKey = `${key}_folder_id` as const
          const id = `${prefix}-assessment-${key}`
          const title = key === 'spam' ? 'Catch spam' : 'Catch phishing'
          const available = targets?.capabilities.destination
          // Junk Email or Spam first, then every folder (Outlook) or label (Gmail).
          const options = [
            ...(targets?.junk ? [targets.junk] : []),
            ...(movement ?? []).filter((item) => item.id !== targets?.junk?.id),
          ]
          const selected =
            config.assessments[folderKey] ?? targets?.junk?.id ?? undefined
          return (
            <div key={key} className="rounded-xl border">
              <div className="flex items-center gap-3 p-4">
                <ShieldAlert className="size-4 shrink-0 text-muted-foreground" />
                <label htmlFor={id} className="flex-1 text-sm font-medium">
                  {title}
                </label>
                <Switch
                  id={id}
                  checked={config.assessments[key]}
                  disabled={available !== true && !config.assessments[key]}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...config,
                      assessments: { ...config.assessments, [key]: checked },
                    })
                  }
                />
              </div>
              {available === false && (
                <p className="-mt-2 px-4 pb-3 pl-11 text-xs text-muted-foreground">
                  Reconnect this inbox to allow this.
                </p>
              )}
              {config.assessments[key] && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t px-4 py-3 sm:pl-11">
                  <span className="text-sm text-muted-foreground">
                    Move to
                  </span>
                  <Select
                    value={selected}
                    onValueChange={(value) =>
                      onChange({
                        ...config,
                        assessments: {
                          ...config.assessments,
                          [folderKey]: value === targets?.junk?.id ? null : value,
                        },
                      })
                    }
                  >
                    <SelectTrigger
                      aria-label={key === 'spam' ? 'Spam folder' : 'Phishing folder'}
                      className="min-w-0 max-w-full flex-1 sm:max-w-xs"
                    >
                      <SelectValue placeholder="Choose a folder" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {resourceName(item, options)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )
        })}
        {catalog.isError && !readOnly && (
          <div className="space-y-2 text-sm">
            <p role="alert">
              Your labels and folders couldn’t load.{' '}
              {classificationRequestMessage(catalog.error)}
            </p>
            <Button variant="outline" size="sm" onClick={refreshResources}>
              Try again
            </Button>
          </div>
        )}
      {editor && catalog.data && (
        <ResourceDialog
          key={`${editor.mode}-${editor.resource?.id ?? editor.kind}`}
          inboxId={inboxId}
          editor={editor}
          catalog={catalog.data}
          allowed={
            !!editor.resource &&
            (permissions.allowed_move_target_ids.includes(editor.resource.id) ||
              permissions.allowed_label_ids.includes(editor.resource.id) ||
              permissions.allowed_category_ids.includes(editor.resource.id))
          }
          onClose={() => setEditor(undefined)}
          onRefresh={refreshResources}
        />
      )}
    </section>
  )
}
